import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Avatar,
  Breadcrumbs,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { 
  Add as AddIcon, 
  Folder as FolderIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
  VpnKey as VpnKeyIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  Check as CheckIcon
} from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { getDataWithToken } from "../../services/GetDataService";
import { postDataWithToken } from "../../services/PostDataService";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import ProjectsIcon from "../../components/icons/ProjectsIcon";

const useStyles = makeStyles((theme) => ({
  container: {
    // Remove custom padding to use global container padding
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
  },
  projectCard: {
    backgroundColor: "#fff",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
    borderRadius: "15px",
    border: "1px solid #E5FFFD",
    "&:hover": {
      boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.1)",
      transform: "translateY(-2px)",
      transition: "all 0.3s ease",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "4rem 2rem",
    backgroundColor: "#f8f9fa",
    borderRadius: "15px",
    border: "2px dashed #E5FFFD",
  },
  createButton: {
    backgroundColor: "#016363",
    color: "white",
    "&:hover": {
      backgroundColor: "#014a4a",
    },
  },
  dialogContent: {
    padding: "2rem 0",
  },
  formField: {
    marginBottom: "2rem",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#016363",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#016363",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#666",
      "&.Mui-focused": {
        color: "#016363",
      },
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "1.5rem 2rem 1rem 2rem",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #E5FFFD",
  },
  dialogActions: {
    padding: "1rem 2rem 2rem 2rem",
    gap: "1rem",
  },
  cancelButton: {
    color: "#666",
    borderColor: "#ddd",
    "&:hover": {
      borderColor: "#999",
      backgroundColor: "#f5f5f5",
    },
  },
  createButtonDialog: {
    backgroundColor: "#016363",
    color: "white",
    padding: "0.75rem 2rem",
    borderRadius: "8px",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#014a4a",
    },
    "&:disabled": {
      backgroundColor: "#ccc",
    },
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    backgroundColor: "#E5FFFD",
    borderRadius: "50%",
    marginBottom: "1rem",
  },
  tableContainer: {
    backgroundColor: "#fff",
    borderRadius: "15px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
    border: "1px solid #E5FFFD",
    overflow: "hidden",
    padding: "1rem 1rem 1rem 1rem",
  },
  tableHeader: {
    backgroundColor: "#f8f9fa",
    "& .MuiTableCell-head": {
      backgroundColor: "#f8f9fa",
      color: "#016363",
      fontWeight: 600,
      fontSize: "0.875rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      borderBottom: "2px solid #E5FFFD",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#fafafa",
    },
  },
  tableCell: {
    borderBottom: "1px solid #f0f0f0",
    padding: "10px",
    paddingLeft: "0px",
  },
  projectNameCell: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  projectAvatar: {
    backgroundColor: "#E5FFFD",
    color: "#016363",
    width: "40px",
    height: "40px",
  },
  projectInfo: {
    display: "flex",
    flexDirection: "column",
  },
  projectTitle: {
    fontWeight: 600,
    color: "#016363",
    fontSize: "1rem",
  },
  projectDesc: {
    color: "#666",
    fontSize: "0.875rem",
    marginTop: "0.25rem",
  },
  credentialsCell: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  credentialBox: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    backgroundColor: "#f8f9fa",
    padding: "0.5rem",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  credentialText: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: "#333",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  copyButton: {
    padding: "4px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#E5FFFD",
    },
  },
  dateCell: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "#666",
    fontSize: "0.875rem",
  },
  statusChip: {
    backgroundColor: "#E8F5E8",
    color: "#2E7D32",
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  projectMeta: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
    marginTop: "1rem",
  },
  metaChip: {
    backgroundColor: "#E5FFFD",
    color: "#016363",
    fontWeight: 500,
  },
}));

const Projects = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { admin_panel_user, logout } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    storeName: "",
    storeDesc: "",
  });
  const [copiedItem, setCopiedItem] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await getDataWithToken(
        "getStoreInformation",
        admin_panel_user,
        logout
      );
      
      if (response.data.code === 200) {
        setProjects(response.data.data.storeInfo || []);
      } else if (response.data.code === 404) {
        setProjects([]);
      } else {
        enqueueSnackbar(response.data.message || "Failed to fetch projects", { variant: "error" });
        setProjects([]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      enqueueSnackbar("Failed to fetch projects", { variant: "error" });
      setProjects([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreateProject = async () => {
    if (!createForm.storeName.trim()) {
      enqueueSnackbar("Please enter project name", { variant: "error" });
      return;
    }

    setCreateLoading(true);
    try {
      const response = await postDataWithToken(
        "createStore",
        {
          storeName: createForm.storeName.trim(),
          storeDesc: createForm.storeDesc.trim(),
        },
        admin_panel_user,
        logout
      );

      if (response.data.code === 200) {
        enqueueSnackbar("Project created successfully!", { variant: "success" });
        setCreateDialogOpen(false);
        setCreateForm({ storeName: "", storeDesc: "" });
        fetchProjects(); // Refresh the list
      } else {
        enqueueSnackbar(response.data.message || "Failed to create project", { variant: "error" });
      }
    } catch (error) {
      console.error("Error creating project:", error);
      enqueueSnackbar("Failed to create project", { variant: "error" });
    }
    setCreateLoading(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyToClipboard = async (text, type, projectId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(`${projectId}-${type}`);
      enqueueSnackbar(`${type === 'storeId' ? 'Store ID' : 'Store Password'} copied to clipboard!`, { variant: "success" });
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (error) {
      enqueueSnackbar("Failed to copy to clipboard", { variant: "error" });
    }
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ py: 1, px: 3, mb: 3 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item xs="auto">
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs="auto">
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: `${theme.palette.primary.light}`,
                  }}
                >
                  <ProjectsIcon color={theme.palette.primary.main} />
                </Avatar>
              </Grid>
              <Grid item xs="auto">
                <Typography
                  variant="base"
                  color="text.main"
                  sx={{ fontWeight: 500 }}
                >
                  Projects
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs="auto">
            <Breadcrumbs
              aria-label="breadcrumb"
            >
              <Link to="/dashboard">Dashboard</Link>
              <Link to="#">Projects</Link>
            </Breadcrumbs>
          </Grid>
        </Grid>
      </Paper>


      {/* Integration Guidance */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: "#F8F9FA", border: "1px solid #E5FFFD" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Box sx={{ 
            backgroundColor: "#E5FFFD", 
            borderRadius: "50%", 
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2L2 7L12 12L22 7L12 2Z"
                stroke="#016363"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 17L12 22L22 17"
                stroke="#016363"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 12L12 17L22 12"
                stroke="#016363"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: "#016363", fontWeight: 600, marginBottom: "0.5rem" }}>
              Integration Guide
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.6 }}>
              Use your project credentials below to integrate CertifyMyID into your application. 
              For detailed integration instructions, SDKs, and code examples, visit our{" "}
              <a 
                href="https://www.certifymyid.com/developers" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: "#016363", 
                  textDecoration: "none", 
                  fontWeight: 600,
                  borderBottom: "1px solid #016363"
                }}
              >
                Developers Documentation
              </a>
              .
            </Typography>
          </Box>
        </Box>
      </Paper>

      {projects.length === 0 ? (
        <Paper className={classes.emptyState}>
          <FolderIcon sx={{ fontSize: 64, color: "#016363", marginBottom: "1rem" }} />
          <Typography variant="h5" sx={{ color: "#016363", marginBottom: "1rem" }}>
            No Projects Found
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", marginBottom: "2rem" }}>
            You don't have any projects yet. Create your first project to get started.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.createButton}
            onClick={() => setCreateDialogOpen(true)}
            size="large"
          >
            Create Your First Project
          </Button>
        </Paper>
      ) : (
        <Box>
          {projects.map((project, index) => (
            <Card key={project.id} className={classes.projectCard} sx={{ marginBottom: "1.5rem" }}>
              <CardContent>
                {/* Project Header */}
                <Box className={classes.projectNameCell} sx={{ marginBottom: "1.5rem" }}>
                  <Avatar className={classes.projectAvatar}>
                    <BusinessIcon />
                  </Avatar>
                  <Box className={classes.projectInfo}>
                    <Typography variant="h6" className={classes.projectTitle}>
                      {project.storeName}
                    </Typography>
                    <Typography variant="body2" className={classes.projectDesc}>
                      {project.storeDesc || "No description provided"}
                    </Typography>
                  </Box>
                </Box>

                {/* Credentials Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Store ID</TableCell>
                        <TableCell>Store Password</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          <Box className={classes.credentialsCell}>
                            <KeyIcon sx={{ fontSize: "1rem", color: "#016363", marginRight: "0.5rem" }} />
                            <Typography className={classes.credentialText} sx={{ maxWidth: "200px" }}>
                              {project.appStoreId}
                            </Typography>
                            <Tooltip title="Copy Store ID">
                              <IconButton
                                size="small"
                                className={classes.copyButton}
                                onClick={() => copyToClipboard(project.appStoreId, 'storeId', project.id)}
                              >
                                {copiedItem === `${project.id}-storeId` ? (
                                  <CheckIcon sx={{ fontSize: "1rem", color: "#4CAF50" }} />
                                ) : (
                                  <CopyIcon sx={{ fontSize: "1rem", color: "#666" }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        
                        <TableCell className={classes.tableCell}>
                          <Box className={classes.credentialsCell}>
                            <VpnKeyIcon sx={{ fontSize: "1rem", color: "#016363", marginRight: "0.5rem" }} />
                            <Typography className={classes.credentialText} sx={{ maxWidth: "200px" }}>
                              {project.appStorePassword}
                            </Typography>
                            <Tooltip title="Copy Store Password">
                              <IconButton
                                size="small"
                                className={classes.copyButton}
                                onClick={() => copyToClipboard(project.appStorePassword, 'storePassword', project.id)}
                              >
                                {copiedItem === `${project.id}-storePassword` ? (
                                  <CheckIcon sx={{ fontSize: "1rem", color: "#4CAF50" }} />
                                ) : (
                                  <CopyIcon sx={{ fontSize: "1rem", color: "#666" }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        
                        <TableCell className={classes.tableCell}>
                          <Box className={classes.dateCell}>
                            <UpdateIcon sx={{ fontSize: "1rem" }} />
                            {formatDate(project.lastUpdate)}
                          </Box>
                        </TableCell>
                        
                        <TableCell className={classes.tableCell}>
                          <Chip
                            label="Active"
                            className={classes.statusChip}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Create Project Action - Bottom Right */}
      {projects.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.createButton}
            onClick={() => setCreateDialogOpen(true)}
          >
            + CREATE PROJECT
          </Button>
        </Box>
      )}

      {/* Create Project Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateForm({ storeName: "", storeDesc: "" });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.1)",
          }
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.iconContainer}>
            <AddIcon sx={{ color: "#016363", fontSize: "1.5rem" }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: "#016363", fontWeight: 600, margin: 0 }}>
              Create New Project
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", marginTop: "0.25rem" }}>
              Set up a new project to get started with CertifyMyID
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent className={classes.dialogContent} sx={{ padding: "2rem" }}>
          <Box sx={{ marginBottom: "2rem", marginTop: "20px" }}>
            <TextField
              fullWidth
              label="Project Name"
              value={createForm.storeName}
              onChange={(e) => setCreateForm({ ...createForm, storeName: e.target.value })}
              className={classes.formField}
              placeholder="e.g., My Travel App, E-commerce Platform"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: "#016363" }} />
                  </InputAdornment>
                ),
              }}
              helperText="Choose a descriptive name for your project"
            />
          </Box>
          
          <Box>
            <TextField
              fullWidth
              label="Project Description"
              value={createForm.storeDesc}
              onChange={(e) => setCreateForm({ ...createForm, storeDesc: e.target.value })}
              className={classes.formField}
              placeholder="Describe what your project does and how it will use identity verification..."
              multiline
              rows={4}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: "flex-start", marginTop: "0.5rem" }}>
                    <DescriptionIcon sx={{ color: "#016363" }} />
                  </InputAdornment>
                ),
              }}
              helperText="Optional: Provide details about your project's purpose and requirements"
            />
          </Box>
        </DialogContent>
        
        <Divider />
        
        <DialogActions className={classes.dialogActions}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            variant="outlined"
            className={classes.cancelButton}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            className={classes.createButtonDialog}
            disabled={createLoading || !createForm.storeName.trim()}
            startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {createLoading ? "Creating..." : "Create Project"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;
